import { LiveCall } from 'redux/slices/liveCalls.slice';
import {
  ActiveCallIcon,
  CallIncomingIcon,
  CallOutgoingIcon,
  ContactBookIcon,
  ExtensionIcon,
  HangUpIcon,
  RecordIcon,
  ThreeDotsMenuIcon,
  OnHoldUnfilledIcon,
  RecordUnfilledIcon,
  InterceptCallUnfilledIcon,
  HangUpUnfilledIcon,
  BargeTransferUnfilledIcon,
  ThirdPartyUnfilledIcon,
  HHAExchangeIcon,
  RecordingIcon,

} from '../../../../assets';
import { Typography } from '../../../common';
import { Dropdown, Modal, Space, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import './liveCallsItemWrapper.scss'
import React, { useEffect, useMemo } from 'react';
import { Utils } from 'utils';
import InfoDrawer from '../InfoDrawer';
import { IntegrationData, useGetIntegrationData } from 'hooks/integrations';
import { CallHistoryRequest } from 'services/callHistory.service';
import CallTimer from '../CallTimer';

interface LiveCallItemWrapperProps {
  callUUID: string;
  name: string;
  ext: string;
  callDuration: string;
  callDescription: JSX.Element | string;
  destination: string;
  callees: LiveCall['callees'];
  callDirection: 'inbound' | 'outbound';
  isRecording: boolean;
  onRecord: (callUUID: string) => void;
  onHangUp: (callUUID: string) => void;
  onClickCall?: (call: LiveCall) => void;
  onTransfer?: (callUUID: string, ext: string, destination: string, callees?: LiveCall['callees'], callerIDNumber?: string, callerIDName?: string) => void;
  callItem?: LiveCall;
}

const LiveCallItemWrapper = ({
  callUUID,
  name,
  ext,
  callDuration,
  callDescription,
  destination,
  callees,
  isRecording,
  callDirection,
  onRecord,
  onHangUp,
  onClickCall,
  onTransfer,
  callItem,
}: LiveCallItemWrapperProps) => {

  const { getIntegrationsData } = useGetIntegrationData();
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const [integrationData, setIntegrationData] = React.useState<IntegrationData[]>([]);
  const callHistoryRequest: CallHistoryRequest = useMemo(() => ({
    callUUID,
    callDirection,
    callerIDNumber: ext,
    destination
  }), [callUUID, callDirection, callees, destination]);

  const phoneNumberForLookup = useMemo(() => {
    if (callDirection === 'inbound') {
      const ringingCallees = callees.filter((item) => item.answerState === "ringing");
      return ringingCallees.length > 0 ? ringingCallees[0].number : '';
    } else {
      return destination;
    }
  }, [callDirection, callees, destination]);

  useEffect(() => {
    if (ext) {
      const data = getIntegrationsData(ext);
      setIntegrationData(data || []);
    } else {
      setIntegrationData([]);
    }
  }, [getIntegrationsData]);


  const menuItems: MenuProps['items'] = [
    // {
    //   label: <Typography variant='body-text-2'>Hold</Typography>,
    //   key: 'hold',
    //   icon: <img src={OnHoldUnfilledIcon} alt="" />
    // },
    {
      label: (
        <div style={{ display: 'flex', gap: '6px', alignItems: "center" }}>
          <Typography variant='body-text-2' style={{ color: 'inherit' }}>
            {isRecording ? "Stop Recording" : "Record"}
          </Typography>
          {isRecording && (
            <img className='animate-pulse' src={RecordingIcon} alt="recording-icon" style={{ width: '18px', height: '18px' }} />
          )}
        </div>
      ),
      key: 'record',
      icon: <img src={RecordUnfilledIcon} alt="" />,
      // disabled: isRecording
    },
    // {
    //   label: <Typography variant='body-text-2'>Intercept Call</Typography>,
    //   key: 'intercept',
    //   icon: <img src={InterceptCallUnfilledIcon} alt="" />
    // },
    // {
    //   label: <Typography variant='body-text-2'>Hang up</Typography>,
    //   key: 'hangup',
    //   icon: <img src={HangUpUnfilledIcon} alt="" />
    // },
    {
      label: <Typography variant='body-text-2'>Transfer</Typography>,
      key: 'barge',
      icon: <img src={BargeTransferUnfilledIcon} alt="" />
    }
  ];

  const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
    if (key === 'hangup') {
      onHangUp(callUUID);
    } else if (key === 'record') {
      onRecord(callUUID);
    }
    else if (key === 'hold') {
    } else if (key === 'intercept') {
    } else if (key === 'barge') {
      onTransfer?.(callUUID, ext, destination, callees, ext, name);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClickCall?.(callItem!);
    }
  };

  const handleClick = () => {
    setIsDrawerOpen(true);
    onClickCall?.(callItem!);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
  };


  return (
    <>
      <li
        key={callUUID}
        className={`live-call-item`}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        data-testid="live-call-item"
        style={{ cursor: 'pointer' }}
      >
        <div className='call-item-card-container'>
          <div className="user-icon-container">
            <img src={ActiveCallIcon} alt="user list icon" />
          </div>
          <div className="call-information-container">
            <div className="user-name-extension-container">
              <Typography variant='subHeading-1' className='user-name'>
                <Tooltip title={name}>
                  {name}
                </Tooltip>
              </Typography>
              <div className="extension-container">
                <img src={ExtensionIcon} alt="extension icon" />
                <Typography variant='small-text-3' className='extension'>
                  <Tooltip title={ext}>
                    {ext}
                  </Tooltip>
                </Typography>
              </div>
            </div>
            <div className="call-status-phone-container">
              <div className="call-status-container">
                <img src={callDirection === 'inbound' ? CallIncomingIcon : CallOutgoingIcon} alt="incoming-call-icon" className='incoming-call-icon' />
                <Tooltip title={callDescription}>
                  <div className='call-description'>{callDescription}</div>
                </Tooltip>
              </div>
              <div className="phone-container">
                <img src={ContactBookIcon} alt="phone book icon" />
                <Typography variant='small-text-3' className='phone'>
                  {callDirection === 'inbound'
                    ? Array.from(new Map(
                      callees
                        .filter((item) => item.answerState === "ringing")
                        .map((item) => [item.number, item])
                    ).values()).map(item => item.number).join(', ')
                    : destination
                  }
                </Typography>
              </div>
            </div>
          </div>
          <div className="time-call-options-container">
            <div className="time-duration-container">
              <CallTimer startTime={callItem?.startTime} />
            </div>
            <div className="call-options-container">
              <div className="selected-icons-container">
                {isRecording && (
                  <img
                    src={RecordIcon}
                    alt="record-icon"
                    className="selected-action-icon"
                  />
                )}

                {integrationData.length > 0 &&
                  integrationData.map((integration) => (
                    <img
                      src={integration?.icon || ''}
                      alt={`${integration?.type} icon`}
                      className="selected-action-icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
 
                        integration?.onClick?.();
                      }}
                      style={{ cursor: 'pointer' }}
                    />
                  ))
                }

              </div>
              <div className="action-buttons">
                <Dropdown
                  menu={{
                    items: menuItems, onClick: (info) => {
                      (info.domEvent as React.MouseEvent).stopPropagation();
                      handleMenuClick(info);
                    }
                  }}
                  trigger={['click']}
                  className='options-dropdown'
                >
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    className="action-button"
                  >
                    <img src={ThreeDotsMenuIcon} alt="menu icon" />
                  </button>
                </Dropdown>
              </div>
            </div>
          </div>
        </div>
      </li>

      <InfoDrawer
        open={isDrawerOpen}
        onCancel={handleCloseDrawer}
        thirdPartyIntegrations={integrationData}
        callHistoryRequest={callHistoryRequest}
      />
    </>
  );
};

export default React.memo(LiveCallItemWrapper, (prevProps, nextProps) => {
  return (
    prevProps.callUUID === nextProps.callUUID &&
    prevProps.name === nextProps.name &&
    prevProps.ext === nextProps.ext &&
    prevProps.destination === nextProps.destination &&
    prevProps.callDirection === nextProps.callDirection &&
    prevProps.isRecording === nextProps.isRecording &&
    prevProps.callDescription === nextProps.callDescription &&
    JSON.stringify(prevProps.callees) === JSON.stringify(nextProps.callees) &&
    prevProps.onRecord === nextProps.onRecord &&
    prevProps.onHangUp === nextProps.onHangUp &&
    prevProps.onClickCall === nextProps.onClickCall &&
    prevProps.callItem?.startTime === nextProps.callItem?.startTime
  );
});

